/**
 * Feature keys mirrored from the backend catalog
 * (cgt-backend/src/feature-flags/feature-catalog.ts). Used for route gating and
 * nav gating so we avoid stringly-typed typos.
 *
 * This is the FEATURE analog of `src/lib/permissions.ts`, and the two answer
 * different questions. A permission asks "may this user do it?"; a feature flag
 * asks "does this tenant have it at all?". A route can be refused by either, and
 * both refusals land on /no-access.
 */
export const FEATURES = {
  fieldCategories: 'fieldCategories',
  equipment: 'equipment',
  integrations: 'integrations',
  patients: 'patients',
  procedures: 'procedures',
  formEngine: 'formEngine',
  authoring: 'authoring',
  procedureTemplates: 'procedure-templates',
  inventory: 'inventory',
  // The Support screen (Title21 Smart Support); off until a tenant opts in.
  support: 'support',
} as const

// Deliberately NOT widened with `| (string & {})`: unlike PermissionKey, which
// tolerates arbitrary keys because roles are data, the feature catalog is a
// closed set compiled into the backend. Widening it would defeat the whole point
// of the const — a typo'd key silently resolves to "unknown ⇒ enabled" in
// FeatureGated and to a permanent 403 on the backend.
export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES]

/**
 * Maps a route to the features that must be on for it to render at all.
 *
 * HARD DEPENDENCIES ONLY — the screen's primary data source, the thing without
 * which the page has nothing to show. A screen that merely *uses* another
 * feature (the form renderer's equipment picker, its MR demographic fields)
 * does NOT list it here; it degrades in place instead, via `useFeatureFlag`.
 *
 * That split is the whole design. These modules are densely interdependent —
 * `proceduresApi` alone is consumed by eight pages — so listing every
 * dependency would mean switching off one module blanks half the app. Blocking
 * on hard deps and degrading soft ones keeps a toggle's blast radius equal to
 * what an admin actually intended.
 *
 * Kept separate from NAV_PERMISSIONS rather than merged into one table: the two
 * are gated at different layers (RBAC vs per-tenant catalog), fail in opposite
 * directions while loading, and a route can carry one, both, or neither.
 */
const NAV_FEATURES: Record<string, FeatureKey[]> = {
  '/procedures': [FEATURES.procedures],
  '/procedures/calendar': [FEATURES.procedures],
  '/integrations': [FEATURES.integrations],
  '/patients': [FEATURES.patients],
  '/equipment': [FEATURES.equipment],
  // The Cleaning page is the Equipment page for rooms (z8nrz7ewt3).
  '/cleaning': [FEATURES.equipment],

  // The form engine: authoring templates and filling them in. Both sides are one
  // key because a definition with nowhere to be submitted (or a submission with
  // no definition) is not a coherent half-product.
  '/form-builder': [FEATURES.formEngine],
  '/form-definitions': [FEATURES.formEngine],
  '/forms': [FEATURES.formEngine],
  '/submissions': [FEATURES.formEngine],

  '/procedure-templates': [FEATURES.procedureTemplates],
  // Process-step execution is served by ProcessStepsController, which sits behind
  // the same `procedure-templates` key — this worklist has no other data source.
  '/my-steps': [FEATURES.procedureTemplates],

  '/inventory': [FEATURES.inventory],
  '/kits': [FEATURES.inventory],
  // Redirects to /inventory, so it must carry the same gate — otherwise the card
  // stays live and bounces the user into a page that then denies.
  '/products': [FEATURES.inventory],

  '/support': [FEATURES.support],
}

/**
 * The features that must be enabled for a nav target (query string ignored).
 * Empty ⇒ never feature-gated, mirroring how `navPermissionsFor` returns [] for
 * a route with no permission requirement.
 */
export function navFeatureFor(to: string): FeatureKey[] {
  const path = to.split('?')[0]
  return NAV_FEATURES[path] ?? []
}
